import { useCallback, useEffect, useMemo, useState } from 'react'
import qs from 'qs'
import { useGetTasksQuery } from '../../../store/api/tasks-api'
import { EndpointQueryParameters } from '../../../types/strapi/strapi'
import { isShipmentAwaitingCuratorApproval } from '../../../helpers/entities/shipment/curator-approval'
import { mergeShipmentApprovals } from '../helpers/merge-shipment-approvals'

const TASKS_COUNT_POLLING_INTERVAL_MS = 60_000

export function buildDesignerTasksQueryParams(
    params: EndpointQueryParameters
): EndpointQueryParameters {
    return {
        filters: params.filters,
        sort: ['createdAt:desc'],
        populate: {
            shipment: {
                populate: {
                    client: true,
                    user_audit: { populate: '*' },
                    machine: true,
                    additional_machines: true,
                    additional_jobs: true,
                    shipment_positions: true,
                    stamp: { populate: '*' },
                    virtualStamp: { populate: ['roller'] },
                    form_set: {
                        populate: {
                            forms: { populate: { form_order: true } },
                        },
                    },
                    materialRequirements: {
                        populate: {
                            material_type: true,
                            configurations: {
                                populate: {
                                    material: true,
                                },
                            },
                        },
                    },
                    lacquers: {
                        populate: {
                            type: true,
                        },
                    },
                    blankPreviewFiles: true,
                    colors: true,
                    tasks: true,
                },
            },
        },
        pagination: params.pagination,
    }
}

export default function useDesignerTasksRefresh(params: EndpointQueryParameters) {
    const [baselineTotal, setBaselineTotal] = useState<number | null>(null)

    const tasksQueryParams = useMemo(() => buildDesignerTasksQueryParams(params), [params])

    const { data } = useGetTasksQuery(tasksQueryParams)

    // Куратор согласует отгрузку в другой сессии — опрашиваем только заблокированные задачи
    // страницы по id, чтобы не подтянуть новые задачи и не сдвинуть список
    const awaitingIds = (data?.data ?? [])
        .filter(task => isShipmentAwaitingCuratorApproval(task.shipment))
        .map(task => task.documentId)

    const { data: approvalData } = useGetTasksQuery(
        {
            filters: { documentId: { $in: awaitingIds } },
            fields: ['documentId'],
            populate: { shipment: { fields: ['approved'] } },
            pagination: { page: 1, pageSize: awaitingIds.length },
        },
        {
            skip: awaitingIds.length === 0,
            pollingInterval: TASKS_COUNT_POLLING_INTERVAL_MS,
            skipPollingIfUnfocused: true,
            refetchOnFocus: true,
        }
    )

    const mergedData = useMemo(
        () => (data && approvalData ? mergeShipmentApprovals(data, approvalData.data) : data),
        [data, approvalData]
    )

    const countQueryParams = useMemo(
        () => ({
            filters: params.filters,
            pagination: { page: 1, pageSize: 1 },
        }),
        [params.filters]
    )

    const { data: countData } = useGetTasksQuery(countQueryParams, {
        pollingInterval: TASKS_COUNT_POLLING_INTERVAL_MS,
        skipPollingIfUnfocused: true,
        refetchOnFocus: true,
        refetchOnReconnect: true,
    })

    const newStatusCountQueryParams = useMemo(
        () => ({
            filters: {
                ...params.filters,
                taskStatus: { $eq: 'Новая' },
            },
            pagination: { page: 1, pageSize: 1 },
        }),
        [params.filters]
    )

    const { data: newStatusCountData } = useGetTasksQuery(newStatusCountQueryParams, {
        pollingInterval: TASKS_COUNT_POLLING_INTERVAL_MS,
        skipPollingIfUnfocused: true,
        refetchOnFocus: true,
        refetchOnReconnect: true,
    })

    useEffect(() => {
        const total = data?.meta.pagination.total

        if (baselineTotal === null && total !== undefined) {
            setBaselineTotal(total)
        }
    }, [baselineTotal, data?.meta.pagination.total])

    const currentTotal = countData?.meta.pagination.total

    const newTasksCount =
        baselineTotal !== null && currentTotal !== undefined
            ? Math.max(0, currentTotal - baselineTotal)
            : 0

    const newStatusCount = newStatusCountData?.meta.pagination.total ?? 0

    const handleRefresh = useCallback(() => {
        const pageSize = params.pagination?.pageSize || 15

        if (params.pagination?.page !== 1) {
            const next = new URL(window.location.href)
            next.searchParams.set('pagination', qs.stringify({ page: 1, pageSize }))
            window.location.assign(next.toString())
            return
        }

        window.location.reload()
    }, [params.pagination?.page, params.pagination?.pageSize])

    return {
        data: mergedData,
        newTasksCount,
        newStatusCount,
        handleRefresh,
    }
}
